/**
 * Sandbox and environment detection attempts (ADR-0012, docs/sandbox.md §6).
 * Owned by Phase 4 lane B.
 *
 * Evidence is every `evasion` event (toString and descriptor probes on
 * shimmed APIs, `navigator.webdriver`, timing loops) plus reads tagged
 * `sandbox-check` (`window.chrome`, `typeof WebAssembly`, outer window
 * size, `Notification.permission`). Those reads are common on their own, so
 * without an `evasion` event it takes three different ones to count.
 */
import type { AuditEvent } from "../../events";
import type { Rule } from "../types";

interface Technique {
  id: string;
  label: string;
  matches(e: AuditEvent): boolean;
}

const TECHNIQUES: readonly Technique[] = [
  {
    id: "tostring",
    label: "toString probing",
    matches: (e) => e.category === "evasion" && e.tags.includes("tostring"),
  },
  {
    id: "descriptor",
    label: "descriptor checks",
    matches: (e) => e.category === "evasion" && e.tags.includes("descriptor"),
  },
  {
    id: "webdriver",
    label: "navigator.webdriver",
    matches: (e) => e.target === "navigator.webdriver",
  },
  {
    id: "timing-loop",
    label: "a timing loop",
    matches: (e) => e.tags.includes("timing-loop"),
  },
  {
    id: "wasm",
    label: "typeof WebAssembly",
    matches: (e) => e.target === "window.WebAssembly",
  },
  {
    id: "chrome",
    label: "window.chrome",
    matches: (e) => e.target === "window.chrome",
  },
  {
    id: "devtools",
    label: "window size (devtools)",
    matches: (e) => e.target === "window.outerWidth" || e.target === "window.outerHeight",
  },
  {
    id: "notification",
    label: "Notification.permission",
    matches: (e) => e.target === "Notification.permission",
  },
];

const isEvidence = (e: AuditEvent) => e.category === "evasion" || e.tags.includes("sandbox-check");

/** Without a direct evasion event, this many different sandbox checks make a finding. */
const MIN_CHECKS = 3;

const sandboxDetection: Rule = {
  id: "evasion.sandbox-detection",
  explanation:
    "The script checked whether it runs in an analysis sandbox or an automated browser: it inspected built-ins for signs of instrumentation (toString, property descriptors) or looked at environment traits headless browsers get wrong (webdriver, window.chrome, WebAssembly, window size, timing). Malware does this to behave innocently when watched; Afterimage answers like a real browser, so the run still shows the real behavior.",
  apply(input) {
    const evidence = input.events.filter(isEvidence);
    const used = TECHNIQUES.filter((t) => evidence.some((e) => t.matches(e)));
    const direct = evidence.some((e) => e.category === "evasion");
    if (!direct && used.length < MIN_CHECKS) return {};
    if (evidence.length === 0) return {};
    const labels = used.map((t) => t.label);
    return {
      findings: [
        {
          severity: "suspicious",
          title: `Sandbox detection: ${labels.length ? labels.join(", ") : "environment checks"}`,
          evidence: evidence.map((e) => e.seq),
          tags: ["evasion", ...used.map((t) => t.id)],
        },
      ],
      escalations: evidence.map((e) => ({ seq: e.seq, severity: "suspicious" as const })),
    };
  },
};

export const EVASION_RULES: readonly Rule[] = [sandboxDetection];
